/* eslint-disable react/display-name */
import {documentToReactComponents, Options} from '@contentful/rich-text-react-renderer'

import {BLOCKS, Document, INLINES, MARKS} from '@contentful/rich-text-types'
import {
  Bold,
  Box,
  Heading,
  Italic,
  Link,
  ListItem,
  OrderedList,
  Text,
  Underlined,
  UnorderedList,
} from '@miro-site/core'
import React from 'react'
import styled from 'styled-components'

export interface RichTextProps {
  document: Document
}

const options: Options = {
  renderMark: {
    [MARKS.BOLD]: text => <Bold>{text}</Bold>,
    [MARKS.ITALIC]: text => <Italic>{text}</Italic>,
    [MARKS.UNDERLINE]: text => <Underlined>{text}</Underlined>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => (
      <Heading as="h1" $variant="h3" $mb={40}>
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Heading as="h2" $variant="h3" $mb={40}>
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Heading as="h3" $variant="h4" $mb={24} $mt={40}>
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Heading as="h4" $variant="h4" $mb={24} $mt={40}>
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <Heading as="h5" $variant="h4" $mb={24} $mt={40}>
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <Heading as="h6" $variant="h4" $mb={24} $mt={40}>
        {children}
      </Heading>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Text $variant="p1" $mb={24}>
        {children}
      </Text>
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <OrderedList $variant="default">
        {React.Children.map(children, child => (
          <ListItem $variant="p1">{child}</ListItem>
        ))}
      </OrderedList>
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <UnorderedList $variant="default">
        {React.Children.map(children, child => (
          <ListItem $variant="p1">{child}</ListItem>
        ))}
      </UnorderedList>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => children,
    [INLINES.HYPERLINK]: (node, children) => (
      <Link href={node.data.uri} $variant="richText" $size="default" $color="default">
        {children}
      </Link>
    ),
  },
}

const RichTextBase: React.FC<RichTextProps> = ({document, ...props}) => {
  return <Box {...props}>{documentToReactComponents(document, options)}</Box>
}

const RichText = styled(RichTextBase)`
  ${Link} * {
    color: currentColor;
    text-decoration: none;
  }
`

export default RichText
